/* eslint-disable complexity */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Popover } from '@mui/material';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';

import { useResources } from '@/contexts/resource-context';
import { CustomSwitch } from '@/features/canvas/components/CustomSwitch';
import { GanttView } from '@/features/canvas/components/GanttView';
import { useDependencyMode } from '@/features/canvas/components/GanttView/hooks/useDependencyMode';
import { HyperlinkForm } from '@/features/canvas/components/HyperlinkForm';
import { InitiativeDetailsDialogContent } from '@/features/canvas/components/InitiativeDialog';
import { SwimlaneView } from '@/features/canvas/components/SwimlaneView';
import { SwimlaneViewButton } from '@/features/canvas/components/SwimlaneViewButton';
import { TimelineEditorDeleteInitiativeDialogs } from '@/features/canvas/components/TimelineEditorDeleteInitiativeDialogs';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { BudgetSidebar } from '@/features/canvas/modules/BudgetSidebar';
import { Canvas } from '@/features/canvas/modules/Canvas';
import { EditorFilterSidebar } from '@/features/canvas/modules/EditorFilterSidebar';
import { FieldType } from '@/features/canvas/types/field-type';
import { Dialog } from '@/features/ui/components/Dialog';
import { Editor } from '@/layouts/Editor';
import { components } from '@/types/api';

const TimelineEditor: React.FC = () => {
  const stageContainerRef = useRef<HTMLDivElement>(null);
  const {
    filteredNodes,
    setFilteredNodes,
    setIsInitiativeDetailDialogOpen,
    isInitiativeDetailDialogOpen,
    isPreview,
    isPublicView,
    isSwimlaneView,
    isGanttView,
    width,
    setWidth,
    selectedNodeId,
    selectedInitiativeId,
    height,
    setHeight,
    lastSavedTime,
    showDependencies,
    setShowDependencies,
    optionCheckedIds,
    setOptionCheckedIds,
    setIsSelectingDependencies,
    setActiveFilterIds,
    setIsMultiFilterEnabled,
    isMultiFilterEnabled,
    setActiveDependencyNodeIds,
    setActiveDependencyConnectionIds,
    viewMode,
    roadmapNodes,
  } = useEditorContext();

  const { logoUrl } = useResources();

  const delay = 60 * 1000;

  useEffect(() => {
    const interval = setInterval(() => {
      console.log(lastSavedTime);
    }, delay);

    return () => clearInterval(interval);
  }, [delay, lastSavedTime]);

  const { t } = useTranslation();

  const [isBasicDeleteInitiativeDialogOpen, setIsBasicDeleteInitiativeDialogOpen] = useState(false);
  const [isDualChoiceDeleteInitiativeDialogOpen, setIsDualChoiceDeleteInitiativeDialogOpen] =
    useState(false);
  const [hasRelationships, setHasRelationships] = useState(false);

  const hyperlinkAnchorRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);

  const [activeHyperlinks, setActiveHyperlinks] = useState<components['schemas']['Hyperlink'][]>(
    []
  );

  // Dependency mode state for Gantt view
  const { dependencyState, toggleDependencyMode } = useDependencyMode();

  // Handle mutual exclusion: disable Gantt dependencies when multi-filtering is enabled
  useEffect(() => {
    if (isGanttView && isMultiFilterEnabled && dependencyState.isDependencyMode) {
      toggleDependencyMode(false);
    }
  }, [isGanttView, isMultiFilterEnabled, dependencyState.isDependencyMode, toggleDependencyMode]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(event => {
      setWidth(event[0].contentBoxSize[0].inlineSize);
      setHeight(event[0].contentBoxSize[0].blockSize);
    });
    const stageContainer = stageContainerRef.current;
    if (stageContainer) {
      resizeObserver.observe(stageContainer);
    }

    return () => {
      if (stageContainer) {
        resizeObserver.unobserve(stageContainer);
      }
    };
  });

  const isCanvasContainerSizeInitialized = width && height;

  const dialogTitle = (() => {
    // If we have selectedInitiativeId, find the initiative by ID
    if (selectedInitiativeId) {
      // First try filteredNodes
      const filteredNode = filteredNodes.find(
        node => node.initiatives?.id === selectedInitiativeId
      );
      if (filteredNode?.initiatives?.title) {
        return filteredNode.initiatives.title;
      }

      // Then try roadmapNodes
      const roadmapNode = roadmapNodes.find(node => node.initiatives?.id === selectedInitiativeId);
      if (roadmapNode?.initiatives?.title) {
        return roadmapNode.initiatives.title;
      }

      // For cross-roadmap initiatives, we won't have the title available here
      // The dialog content will handle fetching the title via API
      return undefined;
    }

    // Fall back to selectedNodeId approach
    if (selectedNodeId) {
      // First try filteredNodes
      const filteredNode = filteredNodes.find(node => Number(node.id) === Number(selectedNodeId));
      if (filteredNode?.initiatives?.title) {
        return filteredNode.initiatives.title;
      }

      // Then try roadmapNodes
      const roadmapNode = roadmapNodes.find(node => Number(node.id) === Number(selectedNodeId));
      return roadmapNode?.initiatives?.title;
    }

    return undefined;
  })();

  const handleOpenHyperlinkPopover = (
    clickPosition: { x: number; y: number },
    hyperlinks: components['schemas']['Hyperlink'][] = []
  ) => {
    const canvas = document.querySelector('canvas');
    if (!canvas) return;

    const adjustedPosition = {
      x: clickPosition.x,
      y: clickPosition.y,
    };

    setActiveHyperlinks(hyperlinks);
    requestAnimationFrame(() => {
      setPopoverPosition(adjustedPosition);
    });
  };

  return (
    <Editor
      setIsBasicDeleteInitiativeDialogOpen={setIsBasicDeleteInitiativeDialogOpen}
      setIsDualChoiceDeleteInitiativeDialogOpen={setIsDualChoiceDeleteInitiativeDialogOpen}
    >
      <CanvasContainer $isSwimlaneView={isSwimlaneView || isGanttView} ref={stageContainerRef}>
        {!isCanvasContainerSizeInitialized && (
          <CanvasLoaderWrapper>
            <StyledLoader />
          </CanvasLoaderWrapper>
        )}

        {isCanvasContainerSizeInitialized && !isSwimlaneView && !isGanttView && (
          <Canvas
            key={`canvas-${isPreview}-${isPublicView}`}
            onOpenHyperlinkPopover={handleOpenHyperlinkPopover}
          />
        )}
        {isCanvasContainerSizeInitialized && isSwimlaneView && <SwimlaneView />}
        {isCanvasContainerSizeInitialized && isGanttView && (
          <GanttView dependencyState={dependencyState} />
        )}

        {/* Show burger menu overlay only for Canvas view in preview/public modes */}
        {isCanvasContainerSizeInitialized &&
          !isSwimlaneView &&
          !isGanttView &&
          (isPreview || isPublicView) && (
            <CanvasViewModeOverlay>
              <SwimlaneViewButton />
            </CanvasViewModeOverlay>
          )}

        {(selectedNodeId || selectedInitiativeId) && (
          <Dialog
            onClose={() => {
              setIsInitiativeDetailDialogOpen(false);
            }}
            open={isInitiativeDetailDialogOpen}
            disablePortal
            keepMounted={false}
            fullScreen={false}
            disableRestoreFocus
            disableEnforceFocus
            disableAutoFocus
            sx={{
              '&.MuiDialog-root': {
                position: 'absolute',
              },
              '& .MuiBackdrop-root': {
                position: 'absolute',
              },
              '& .MuiDialog-paper': {
                marginTop: '40px',
                padding: '2rem 2rem 4rem 2rem',
                borderRadius: '2rem',
                minWidth: hasRelationships ? '1110px' : '900px',
                maxHeight: '73%',
                overflow: 'visible',
                position: 'relative',
              },
            }}
          >
            <InitiativeDetailsDialogContent
              dialogTitle={dialogTitle}
              onRelationshipsStatusChange={setHasRelationships}
            />
          </Dialog>
        )}
      </CanvasContainer>
      <ContentWrapper>
        <LogoWrapper>
          <StyledImage src={logoUrl} />
        </LogoWrapper>

        {viewMode === 'filter' ? <EditorFilterSidebar /> : <BudgetSidebar />}

        {/* Dependencies toggle for Sunray, Swimlane and Gantt views */}
        <DependencyToggle>
          <DependencyTitle>{t('editor.filter_sidebar.dependencies')}</DependencyTitle>
          <CustomSwitch
            isChecked={isGanttView ? dependencyState.isDependencyMode : showDependencies}
            onChange={() => {
              if (isGanttView) {
                // Gantt view - use our dependency mode with mutual exclusion logic
                const newDependencyMode = !dependencyState.isDependencyMode;

                if (newDependencyMode) {
                  // If turning dependencies ON, disable multi-filtering (copy Sunray logic)
                  setIsMultiFilterEnabled(false);

                  if (optionCheckedIds.length > 0) {
                    setActiveFilterIds([]);
                    setOptionCheckedIds([]);

                    setFilteredNodes(prevNodes =>
                      prevNodes.map(node => ({
                        ...node,
                        shape: 'circle',
                        color: '',
                        size: undefined,
                      }))
                    );
                  }
                }

                toggleDependencyMode(newDependencyMode);
              } else {
                // Sunray view - use the original logic
                setOptionCheckedIds([]);
                setIsSelectingDependencies(false);
                setShowDependencies(prev => {
                  const newState = !prev;

                  if (newState) {
                    setIsMultiFilterEnabled(false);

                    if (optionCheckedIds.length > 0) {
                      setActiveFilterIds([]);
                      setOptionCheckedIds([]);

                      setFilteredNodes(prevNodes =>
                        prevNodes.map(node => ({
                          ...node,
                          shape: 'circle',
                          color: '',
                          size: undefined,
                        }))
                      );
                    }
                  } else {
                    setActiveDependencyNodeIds(new Set());
                    setActiveDependencyConnectionIds(new Set());
                  }

                  return newState;
                });
              }
            }}
          />
        </DependencyToggle>
      </ContentWrapper>
      <Copyright>
        {t('copyright', {
          currentYear: dayjs().year(),
        })}
      </Copyright>

      <div
        ref={hyperlinkAnchorRef}
        style={{
          position: 'absolute',
          top: popoverPosition?.y ?? -9999,
          left: popoverPosition?.x ?? -9999,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
      />

      {popoverPosition && hyperlinkAnchorRef.current && (
        <Popover
          open={!!popoverPosition}
          anchorEl={hyperlinkAnchorRef.current}
          onClose={() => setPopoverPosition(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: theme => ({
              p: 2,
              maxWidth: 220,
              position: 'relative',
              overflow: 'visible',
              padding: 1,
              borderRadius: 2,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: 32,
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: `16px solid ${theme.palette.background.paper}`,
                zIndex: 1,
              },
            }),
          }}
        >
          <HyperlinkForm
            hyperlinks={activeHyperlinks}
            setHyperlinks={setActiveHyperlinks}
            fieldId={selectedNodeId!} // <-- the initiative id or field id
            fieldType={FieldType.InitiativeField}
          />
        </Popover>
      )}
      <TimelineEditorDeleteInitiativeDialogs
        isDualChoiceDeleteInitiativeDialogOpen={isDualChoiceDeleteInitiativeDialogOpen}
        setIsBasicDeleteInitiativeDialogOpen={setIsBasicDeleteInitiativeDialogOpen}
        setIsDualChoiceDeleteInitiativeDialogOpen={setIsDualChoiceDeleteInitiativeDialogOpen}
        isBasicDeleteInitiativeDialogOpen={isBasicDeleteInitiativeDialogOpen}
      />
    </Editor>
  );
};

const ContentWrapper = styled.div`
  padding: 2rem 2rem 0 0;
  margin-bottom: 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoWrapper = styled.div`
  margin: 2rem 0 4rem;
  padding: 1rem 3rem;
  width: 250px;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
`;

const CanvasLoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CanvasContainer = styled.div<{ $isSwimlaneView: boolean }>`
  flex: 5 1 calc(100% - 280px);

  ${({ $isSwimlaneView }) =>
    !$isSwimlaneView &&
    css`
      overflow: hidden;
    `}
`;

const Copyright = styled.div`
  position: absolute;
  width: 250px;
  bottom: 0;
  right: 2rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 0 1.5rem 0;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
`;

const StyledLoader = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;

const DependencyToggle = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.5rem 0.5rem 1.5rem;
`;

const DependencyTitle = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
`;

const CanvasViewModeOverlay = styled.div`
  position: absolute;
  top: 40px;
  left: 40px;
  z-index: 10;
  pointer-events: auto;
`;

export { TimelineEditor };
